import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Chip, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

import {
  TOUR_OFFER_TRANSFER_TYPE_LABELS,
  TourOfferItineraryDayInput,
  TourOfferItineraryInput,
  TourOfferTransferInput,
  TourOfferTransferType,
} from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourItineraryDay from './NativeTourItineraryDay';
import NativeTourLocationSearch from './NativeTourLocationSearch';
import { itineraryProblems } from './nativeTourForm';

interface Props {
  value: TourOfferItineraryInput;
  onChange: (value: TourOfferItineraryInput) => void;
  lengthInDays: number | null;
  hosts: Array<{ svcImageId: string; name: string }>;
}

const NOT_APPLICABLE = 'notApplicable';
const TRANSFER_TYPES = Object.keys(TOUR_OFFER_TRANSFER_TYPE_LABELS) as Array<TourOfferTransferType>;
const EMPTY_TRANSFER: TourOfferTransferInput = {
  arrivalAirport: null,
  arrivalTransferType: null,
  departureAirport: null,
  departureTransferType: null,
};

const NEW_DAY: TourOfferItineraryDayInput = {
  title: '',
  description: '',
  duration: 1,
  accommodation: null,
  meals: [],
  locations: [],
  carouselImages: [],
  specialCallOuts: [],
};

/** A tour option's base itinerary, day by day, and its arrival and departure transfer. */
export default function NativeTourItineraryEditor({ value, onChange, lengthInDays, hosts }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const problems = itineraryProblems(value, lengthInDays);
  const totalDays = value.days.reduce((total, day) => total + (Number.isFinite(day.duration) ? day.duration : 0), 0);
  const setDays = (days: Array<TourOfferItineraryDayInput>) => onChange({ ...value, days });
  const transfer = value.transfer ?? EMPTY_TRANSFER;
  const setTransfer = (change: Partial<TourOfferTransferInput>) => {
    const next = { ...transfer, ...change };
    onChange({ ...value, transfer: Object.values(next).some((field) => field !== null) ? next : null });
  };

  const startDays = value.days.reduce<Array<number>>(
    (starts, day, index) => [
      ...starts,
      index === 0 ? 1 : starts[index - 1] + Math.max(value.days[index - 1].duration, 1),
    ],
    [],
  );

  const transferType = (label: string, field: 'arrivalTransferType' | 'departureTransferType') => (
    <TextField
      select
      label={label}
      value={transfer[field] ?? NOT_APPLICABLE}
      onChange={(event) =>
        setTransfer({
          [field]: event.target.value === NOT_APPLICABLE ? null : (event.target.value as TourOfferTransferType),
        })
      }
      fullWidth
    >
      <MenuItem value={NOT_APPLICABLE}>Not applicable</MenuItem>
      {TRANSFER_TYPES.map((type) => (
        <MenuItem key={type} value={type}>
          {TOUR_OFFER_TRANSFER_TYPE_LABELS[type]}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Stack spacing={3}>
      <NativeTourCard
        title="Days"
        description="The itinerary customers see. Saved to the same days the manifest and PDFs read."
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={problems.total ? 'error' : 'success'}
              variant="outlined"
              label={lengthInDays === null ? `${totalDays} days` : `${totalDays} of ${lengthInDays} days`}
            />
            {problems.total && (
              <Typography variant="body2" color="error">
                {problems.total}
              </Typography>
            )}
          </Stack>
          {value.days.map((day, index) => (
            <NativeTourItineraryDay
              key={index}
              day={day}
              startDay={startDays[index]}
              problem={problems.days[index]}
              hosts={hosts}
              isFirst={index === 0}
              isLast={index === value.days.length - 1}
              expanded={expanded === index}
              onExpand={(isOpen) => setExpanded(isOpen ? index : null)}
              onChange={(next) => setDays(value.days.map((other, i) => (i === index ? next : other)))}
              onMove={(by) => {
                const days = [...value.days];
                const [moved] = days.splice(index, 1);
                days.splice(index + by, 0, moved);
                setDays(days);
                setExpanded(index + by);
              }}
              onCopy={() => {
                setDays([...value.days.slice(0, index + 1), { ...day }, ...value.days.slice(index + 1)]);
                setExpanded(index + 1);
              }}
              onRemove={() => {
                setDays(value.days.filter((_, i) => i !== index));
                setExpanded(null);
              }}
            />
          ))}
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setDays([...value.days, NEW_DAY]);
                setExpanded(value.days.length);
              }}
            >
              Add day
            </Button>
          </Box>
        </Stack>
      </NativeTourCard>

      <NativeTourCard title="Transfers" description="Getting customers from and to the airport, kept on the first day.">
        {value.days.length === 0 && <Alert severity="info">Add a day first; the transfer is kept on it.</Alert>}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <NativeTourLocationSearch
                label="Arrival airport"
                multiple={false}
                types={['airport']}
                value={transfer.arrivalAirport ? [transfer.arrivalAirport] : []}
                onChange={([airport]) => setTransfer({ arrivalAirport: airport ?? null })}
              />
              {transferType('Arrival transfer', 'arrivalTransferType')}
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <NativeTourLocationSearch
                label="Departure airport"
                multiple={false}
                types={['airport']}
                value={transfer.departureAirport ? [transfer.departureAirport] : []}
                onChange={([airport]) => setTransfer({ departureAirport: airport ?? null })}
              />
              {transferType('Departure transfer', 'departureTransferType')}
            </Stack>
          </Grid>
        </Grid>
      </NativeTourCard>
    </Stack>
  );
}
